import { KeyRound, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { Brand } from "../app/Brand";
import { LocaleMenu } from "../app/LocaleMenu";
import { useT } from "../i18n/core";
import { loginMessages } from "./login.i18n";
import { resetMessages } from "./reset.i18n";

// O segredo chega no fragmento (/reset#ebr1_…), que não vai ao servidor.
function readToken(): string {
  const hash = window.location.hash;
  const token = hash.slice(hash.lastIndexOf("#") + 1);
  return token.startsWith("ebr1_") ? token : "";
}

export function ResetPage() {
  const tl = useT(loginMessages);
  const [token] = useState(readToken);

  useEffect(() => {
    if (window.location.hash.startsWith("#ebr1_")) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, []);

  return (
    <div className="login">
      <aside className="login-side">
        <Brand />
        <div className="login-quote">
          <h2>{tl.headline}</h2>
          <p>{tl.pitch}</p>
        </div>
        <span />
      </aside>
      <div className="login-form">
        <div className="login-lang"><LocaleMenu variant="plain" /></div>
        <div className="login-card">{token ? <ConfirmForm token={token} /> : <RequestForm />}</div>
      </div>
    </div>
  );
}

function RequestForm() {
  const t = useT(resetMessages);
  const api = useApi();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 18 }} noValidate>
      <div>
        <h1>{t.requestTitle}</h1>
        <p className="ink-2" style={{ marginTop: 6 }}>{t.requestLede}</p>
      </div>
      {sent ? (
        <p className="callout ok" role="status">{t.sent}</p>
      ) : (
        <>
          <div className="field">
            <label htmlFor="reset-email">{t.email}</label>
            <input id="reset-email" className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </div>
          {error && <p className="field error" role="alert">{error}</p>}
          <button className="btn btn-primary" type="submit" style={{ height: 40 }} disabled={busy || !email.includes("@")}>
            <Mail aria-hidden="true" /> {busy ? t.working : t.send}
          </button>
        </>
      )}
      <button className="btn btn-ghost" type="button" onClick={() => navigate("/login")}>{t.backToLogin}</button>
    </form>
  );
}

function ConfirmForm({ token }: { token: string }) {
  const t = useT(resetMessages);
  const api = useApi();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError(t.mismatch);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.confirmPasswordReset({ token, newPassword: password, code });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={{ display: "grid", gap: 18 }}>
        <h1>{t.confirmTitle}</h1>
        <p className="callout ok" role="status">{t.done}</p>
        <button className="btn btn-primary" type="button" style={{ height: 40 }} onClick={() => navigate("/login", { replace: true })}>{t.backToLogin}</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 16 }} noValidate>
      <div>
        <h1>{t.confirmTitle}</h1>
        <p className="ink-2" style={{ marginTop: 6 }}>{t.confirmLede}</p>
      </div>
      <div className="field">
        <label htmlFor="reset-new">{t.newPassword}</label>
        <input id="reset-new" className="input" type="password" autoComplete="new-password" minLength={12} maxLength={256} value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
        <span className="hint">{t.newPasswordHint}</span>
      </div>
      <div className="field">
        <label htmlFor="reset-confirm">{t.confirmPassword}</label>
        <input id="reset-confirm" className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="reset-code">{t.code}</label>
        <input id="reset-code" className="input code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
        <span className="hint">{t.lostAuthenticator}</span>
      </div>
      {error && <p className="field error" role="alert">{error}</p>}
      <button className="btn btn-primary" type="submit" style={{ height: 40 }} disabled={busy || password.length < 12 || code.length !== 6}>
        <KeyRound aria-hidden="true" /> {busy ? t.working : t.save}
      </button>
    </form>
  );
}
